import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import CloseIcon from '@material-ui/icons/Close';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import Button from 'UI/button';
import axios from 'axios';
import { baseURL } from 'utils/apiEndpoints';
import { TIMEOUT } from 'utils/consts';

class CheckAvailabilityModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isSending: false,
    };
  }

  handleClose = () => {
    const { closePricingModal } = this.props;
    closePricingModal();
  };

  handleSendAvailabilityCheck = async () => {
    const {
      showSnackbar, closePricingModal, itineraryId,
    } = this.props;

    this.setState({ isSending: true });

    try {
      // Make POST API call
      await axios({
        url: `${baseURL}itineraries/${itineraryId}/sendAvailabilityEmail`,
        method: 'post',
        timeout: TIMEOUT,
      });

      showSnackbar('Availability check sent successfully', 'success');
      closePricingModal();
    } catch (error) {
      showSnackbar('Failed to send availability check. Please try again.', 'error');
    } finally {
      this.setState({ isSending: false });
    }
  };

  render() {
    const { classes, isOpen } = this.props;
    const { isSending } = this.state;

    return (
      <Dialog
        open={isOpen}
        onClose={this.handleClose}
        maxWidth="md"
        fullWidth
        classes={{
          paper: classes.dialogPaper,
        }}
      >
        <div className={classes.modalContainer}>
          {/* Header */}
          <div className={classes.header}>
            <div className={classes.titleContainer}>
              <EventOutlinedIcon className={classes.headerIcon} />
              <span className={classes.title}>Check Availability</span>
            </div>
            <CloseIcon 
              className={classes.closeIcon} 
              onClick={this.handleClose}
            />
          </div>

          {/* Content */}
          <div className={classes.content}>
            {/* Instructions */}
            <div className={classes.instructionsText}>
              Initiate an availability check with our travel supply partners via email. Stay alert for supplier responses and escalate to your Mentor/TL if there are delays.
            </div>

            {/* Best Practices Section */}
            <div className={classes.bestPracticesContainer}>
              <div className={classes.bestPracticesTitle}>Best Practices</div>
              <ul className={classes.bestPracticesList}>
                <li className={classes.bestPracticesItem}>
                  <span className={classes.mandatoryText}>Mandatory:</span> Do the check <span className={classes.boldText}>before collecting payment</span> from the customer. This prevents backtracking, escalations, and a poor client experience.
                </li>
                <li className={classes.bestPracticesItem}>
                  <span className={classes.boldText}>Do it earlier</span> when:
                  <ul className={classes.nestedList}>
                    <li>Handling large groups</li>
                    <li>Travel dates are approaching soon</li>
                    <li>Dates overlap with holidays, festivals, or other special occasions</li>
                  </ul>
                </li>
              </ul>
            </div>

            {/* Send Button */}
            <Button
              variant="contained"
              className={classes.sendButton}
              onClick={this.handleSendAvailabilityCheck}
              disabled={isSending}
            >
              {isSending ? 'Sending ...' : 'Send Availability Check'}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  dialogPaper: {
    borderRadius: 12,
    width: 700,
    margin: 16,
  },
  modalContainer: {
    background: theme.colors.white,
    borderRadius: 12,
    fontFamily: 'Sofia-pro, sans-serif',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  headerIcon: {
    fontSize: 24,
    color: theme.colors.primary,
    marginRight: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: '#32302F',
    fontFamily: 'Sofia-pro, sans-serif',
  },
  closeIcon: {
    fontSize: 24,
    color: '#666666',
    cursor: 'pointer',
    '&:hover': {
      color: '#333333',
    },
  },
  content: {
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  instructionsText: {
    fontSize: 16,
    color: '#32302F',
    lineHeight: 1.6,
    fontFamily: 'Sofia-pro, sans-serif',
  },
  bestPracticesContainer: {
    marginTop: 8,
  },
  bestPracticesTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#32302F',
    marginBottom: 16,
    fontFamily: 'Sofia-pro, sans-serif',
  },
  bestPracticesList: {
    margin: 0,
    paddingLeft: 20,
    listStyleType: 'disc',
  },
  bestPracticesItem: {
    fontSize: 16,
    color: '#32302F',
    lineHeight: 1.8,
    marginBottom: 12,
    fontFamily: 'Sofia-pro, sans-serif',
  },
  mandatoryText: {
    fontWeight: 700,
  },
  boldText: {
    fontWeight: 700,
  },
  nestedList: {
    marginTop: 8,
    marginLeft: 20,
    paddingLeft: 20,
    listStyleType: 'disc',
  },
  sendButton: {
    backgroundColor: '#0090B5',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 24,
    height: 42,
    marginTop: 20,
    padding: '0 40px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Sofia-pro, sans-serif',
    alignSelf: 'flex-start',
    '&:hover': {
      backgroundColor: '#007A94',
    },
    '&:disabled': {
      backgroundColor: '#DDDDDD',
      cursor: 'not-allowed',
    },
  },
});

CheckAvailabilityModal.propTypes = {
  classes: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closePricingModal: PropTypes.func.isRequired,
  showSnackbar: PropTypes.func.isRequired,
  itineraryId: PropTypes.string.isRequired,
};

export default withStyles(styles)(CheckAvailabilityModal);
